/* eslint-disable react-refresh/only-export-components --
   The Provider component and useDashboardScope hook are intentionally
   colocated so callers import one symbol. Splitting forces a re-export
   shim and adds zero value beyond satisfying fast-refresh. */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import type { DashboardWidgetScope } from '@/api/endpoints/dashboard';

import {
  clearDashboardScopeSession,
  readDashboardScopeSession,
  writeDashboardScopeSession,
} from '../lib/dashboard-scope-session';

/**
 * DashboardScopeContext — PR #49 DASH-W1-5 + DEF-306 sticky session.
 *
 * SSI-only scope override that re-targets every widget on the canvas
 * to a chosen Owner Company or Building Entity. The override is
 * client-side; it does NOT change the authenticated identity (per
 * PRD §6 — "the SSI user remains authenticated as themselves").
 * Audit-relevant writes still record the SSI actor.
 *
 * Widgets that participate in scope read `useDashboardScope()` and
 * fold the resulting companyId / buildingId into their query keys.
 * Widgets that don't read the context behave exactly as before.
 *
 * Portfolio scope persists in sessionStorage for the tab lifetime
 * (DEF-306) so navigate-away / reload keeps the lens. It is NOT
 * written to UserPreferences — a scope override is a temporary lens.
 * Property dashboard (`initialBuildingId`) skips session hydrate/write.
 */

export type DashboardScopeMode = 'none' | 'company' | 'building' | 'user';

export interface DashboardScope {
  mode: DashboardScopeMode;
  companyId: number | null;
  companyName: string | null;
  buildingId: number | null;
  buildingName: string | null;
  /** SSI "scope-as-user" target (US-4.1). `null` unless viewing the dashboard as a chosen user. */
  scopeAsUserId: number | null;
  scopeAsUserName: string | null;
  scopeAsUserEmail: string | null;
}

interface DashboardScopeContextValue extends DashboardScope {
  setCompanyScope: (companyId: number, companyName: string) => void;
  setBuildingScope: (buildingId: number, buildingName: string) => void;
  /** Enter the SSI read-only scope-as-user lens (US-4.1). Mutually exclusive with company/building. */
  setUserScope: (userId: number, displayName: string, email: string) => void;
  /** Clear owner/entity scope only — preserves an active scope-as-user lens (US-4.2 filterMode toggle). */
  clearOwnerEntityScope: () => void;
  clear: () => void;
  /**
   * The scope override as widget query params — the single object every widget folds into its
   * TanStack Query key AND passes to `dashboardApi`. Stable identity per scope change so query keys
   * only churn when the scope actually changes.
   */
  params: DashboardWidgetScope;
}

const INITIAL_SCOPE: DashboardScope = {
  mode: 'none',
  companyId: null,
  companyName: null,
  buildingId: null,
  buildingName: null,
  scopeAsUserId: null,
  scopeAsUserName: null,
  scopeAsUserEmail: null,
};

const DashboardScopeReact = createContext<DashboardScopeContextValue | null>(null);

function resolveInitialScope(initialBuildingId: number | null): DashboardScope {
  // Property dashboard owns its building from the route — never hydrate SSI session.
  if (initialBuildingId != null && initialBuildingId > 0) {
    return {
      ...INITIAL_SCOPE,
      mode: 'building',
      buildingId: initialBuildingId,
      buildingName: null,
    };
  }
  return readDashboardScopeSession() ?? INITIAL_SCOPE;
}

function commitScope(
  next: DashboardScope,
  setScope: (scope: DashboardScope) => void,
  persistSession: boolean,
): void {
  setScope(next);
  if (!persistSession) return;
  if (next.mode === 'none') {
    clearDashboardScopeSession();
    return;
  }
  writeDashboardScopeSession(next);
}

export function DashboardScopeProvider({
  children,
  initialBuildingId = null,
}: {
  children: ReactNode;
  /** When set (property dashboard), widgets scope to this building without the SSI filter bar. */
  initialBuildingId?: number | null;
}) {
  // Property dashboard must not read/write the portfolio SSI session key.
  const persistSession = initialBuildingId == null || initialBuildingId <= 0;
  const [scope, setScope] = useState<DashboardScope>(() =>
    resolveInitialScope(initialBuildingId ?? null),
  );

  const value = useMemo<DashboardScopeContextValue>(
    () => ({
      ...scope,
      setCompanyScope: (companyId, companyName) =>
        commitScope(
          {
            ...INITIAL_SCOPE,
            mode: 'company',
            companyId,
            companyName,
          },
          setScope,
          persistSession,
        ),
      setBuildingScope: (buildingId, buildingName) =>
        commitScope(
          {
            ...INITIAL_SCOPE,
            mode: 'building',
            buildingId,
            buildingName,
          },
          setScope,
          persistSession,
        ),
      setUserScope: (userId, displayName, email) =>
        commitScope(
          {
            ...INITIAL_SCOPE,
            mode: 'user',
            scopeAsUserId: userId,
            scopeAsUserName: displayName,
            scopeAsUserEmail: email,
          },
          setScope,
          persistSession,
        ),
      clearOwnerEntityScope: () => {
        if (scope.mode === 'company' || scope.mode === 'building') {
          commitScope(INITIAL_SCOPE, setScope, persistSession);
        }
      },
      clear: () => commitScope(INITIAL_SCOPE, setScope, persistSession),
      params: {
        companyId: scope.companyId,
        buildingId: scope.buildingId,
        scopeAsUserId: scope.scopeAsUserId,
      },
    }),
    [scope, persistSession],
  );

  return (
    <DashboardScopeReact.Provider value={value}>{children}</DashboardScopeReact.Provider>
  );
}

export function useDashboardScope(): DashboardScopeContextValue {
  const ctx = useContext(DashboardScopeReact);
  if (!ctx) {
    // Safe fallback for unit tests / non-Dashboard surfaces that
    // accidentally call into this hook. Returns the "no scope"
    // shape with no-op setters.
    return {
      ...INITIAL_SCOPE,
      setCompanyScope: () => {},
      setBuildingScope: () => {},
      setUserScope: () => {},
      clearOwnerEntityScope: () => {},
      clear: () => {},
      params: { companyId: null, buildingId: null, scopeAsUserId: null },
    };
  }
  return ctx;
}
